import React, { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Col, Row, Form as BsForm, Badge } from 'react-bootstrap'

import { useFetch } from '../../../Common'
import { useNotifications } from '../../../Notifications'

import { PackageGetResponse, PackagePriceDetails, PackageIntlDetails, PackageVoucherDetails } from '../../../../../cz.vegilove.api/src/_api-models'

import { API_URL, CONTENT_SOURCES } from '../../AppConfig'
import { Currency, CurrencyNames } from '../../AppTypes'

export type ItemDetails = {
    packageId: number
    price: number
    isVoucher: boolean
    forceVoucher: boolean
}

type OrderItemProps = {
    packageId: number
    currency: Currency
    setItemDetails: ( packPriceDetails: ItemDetails | undefined ) => void
}

export const OrderItem = ({ packageId, currency, setItemDetails }: OrderItemProps ) => {

    const intl = useIntl()

    const { addNotification } = useNotifications()

    const [ packIntlDetails, setPackIntlDetails ] = useState<PackageIntlDetails | undefined>()
    const [ packPriceDetails, setPackPriceDetails ] = useState<PackagePriceDetails | undefined>()
    const [ canVoucher, setCanVoucher ] = useState<PackageVoucherDetails | undefined>( undefined )
    const [ forceVoucher, setForceVoucher ] = useState( false )
    const [ isVoucher, setIsVoucher ] = useState( false )

    // load package details to be ordered
    const { state: packDetailsState } = useFetch<PackageGetResponse[]>({
        url: `${ API_URL }/package/${ packageId || '' }`,
        noAutoRun: !packageId,
        onSuccess: ( state ) => {
            if( 
                state.data && state.data[ 0 ] && state.data[ 0 ].ext_data 
                && state.data[ 0 ].ext_data.intl && state.data[ 0 ].ext_data?.intl?.length > 0 
                && state.data[ 0 ].ext_data.prices && state.data[ 0 ].ext_data?.prices?.length > 0
            ) {
                setPackIntlDetails( state.data[ 0 ].ext_data?.intl?.find( i => i.locale === intl.locale ) )
                setPackPriceDetails( state.data[ 0 ].ext_data?.prices?.find( p => p.currency === currency ) )
                setCanVoucher( state.data[ 0 ].ext_data?.vouchers?.find( v => v.locale === intl.locale ) )
                setForceVoucher( state.data[ 0 ].ext_data?.forceVoucher || false )
                if( state.data[ 0 ].ext_data?.forceVoucher ) setIsVoucher( true )
                // last but not least - push data to parent
                setItemDetails({
                    packageId,
                    price: state.data[ 0 ].ext_data?.prices?.find( p => p.currency === currency )?.price || NaN,
                    isVoucher: state.data[ 0 ].ext_data?.forceVoucher || false,
                    forceVoucher: state.data[ 0 ].ext_data?.forceVoucher || false
                })
            } 
            else {
                addNotification({ variant: 'danger', category: 'app', content: intl.formatMessage( { description: 'App.Order.PackDownloadError', defaultMessage: 'We were not able to retrieve details for your chosen product. Please, try it once more or contact us via email.' } ) })
            }
        },
        onError: () => {
            addNotification({ variant: 'danger', category: 'app', content: intl.formatMessage( { description: 'App.Order.PackDownloadError', defaultMessage: 'We were not able to retrieve details for your chosen product. Please, try it once more or contact us via email.' } ) })
        }
    }, [ packageId ] )

    useEffect( () => {
        if( packDetailsState.success && packDetailsState.data ) {
            setPackIntlDetails( packDetailsState.data[ 0 ].ext_data?.intl?.find( i => i.locale === intl.locale ) )
            setCanVoucher( packDetailsState.data[ 0 ].ext_data?.vouchers?.find( v => v.locale === intl.locale ) )
            // last but not least - push data to parent
            setItemDetails({
                packageId,
                price: packDetailsState.data[ 0 ].ext_data?.prices?.find( p => p.currency === currency )?.price || NaN,
                isVoucher: isVoucher || packDetailsState.data[ 0 ].ext_data?.forceVoucher || false,
                forceVoucher: packDetailsState.data[ 0 ].ext_data?.forceVoucher || false
            })
        }
    }, [ intl ])

    useEffect( () => {
        if( packDetailsState.success && packDetailsState.data ) {
            setPackPriceDetails( packDetailsState.data[ 0 ].ext_data?.prices?.find( p => p.currency === currency ) )
            // last but not least - push data to parent
            setItemDetails({
                packageId,
                price: packDetailsState.data[ 0 ].ext_data?.prices?.find( p => p.currency === currency )?.price || NaN,
                isVoucher: isVoucher || packDetailsState.data[ 0 ].ext_data?.forceVoucher || false,
                forceVoucher: packDetailsState.data[ 0 ].ext_data?.forceVoucher || false
            })
        }
    }, [ currency ])

    return <>
        { packIntlDetails && packPriceDetails && <Row className='py-1'>
            <Col size={ 12 } md={ 3 }>
                <img 
                    className='w-100' 
                    src={ canVoucher && isVoucher 
                        ? CONTENT_SOURCES[ canVoucher.source ] + canVoucher.filename
                        : CONTENT_SOURCES[ packIntlDetails.thumb.source ] + packIntlDetails.thumb.filename 
                    } 
                    alt={ canVoucher && isVoucher 
                        ? intl.formatMessage({ description: 'App.Order.VoucherThumbAlt', defaultMessage: 'Voucher thumbnail' })
                        : intl.formatMessage({ description: 'App.Order.PorductThumbAlt', defaultMessage: 'Product thumbnail' }) 
                    } 
                />
            </Col>
            <Col size={ 8 } md={ 6 }>
                <div>{ packIntlDetails.name }</div>
                { canVoucher && <div><BsForm.Check type='checkbox'
                    checked={ isVoucher }
                    onChange={ () => {
                        setIsVoucher( !isVoucher ) 
                        // and update data for parent
                        setItemDetails({
                            packageId,
                            price: packPriceDetails?.price || NaN,
                            isVoucher: !isVoucher,
                            forceVoucher
                        })        
                    }}
                    disabled={ forceVoucher }
                    label={ <FormattedMessage description='App.Order.IsVoucher' defaultMessage='Order as voucher' /> }
                /></div> }
                { forceVoucher && <div>
                    <Badge bg='radish'><FormattedMessage description='App.Order.PresaleOffer' defaultMessage='Pre-sale offer' /></Badge>&nbsp;
                    <FormattedMessage description='App.Order.PresaleOfferInfo' defaultMessage='estimated date of launch 2023/01 = you can only purchase a voucher of the value of the course' />
                </div>}
            </Col>
            <Col size={ 4 } md={ 3 } className="text-end">{ ( currency !== 'CZK' ? CurrencyNames[ currency ] : '' ) + packPriceDetails.price.toFixed( 2 ) + ( currency === 'CZK' ? ' Kč' : '' ) }</Col>
        </Row> }
        { !packPriceDetails && <Row>
            <Col size={ 12 } md={ 3 }>
            </Col>
            <Col size={ 8 } md={ 6 }>
                <FormattedMessage description='App.Order.UnknownItem' defaultMessage='Unknown order item.' />
            </Col>
        </Row>}
    </>
}